import { z } from 'zod';
import { moneyMinorSchema, uuidSchema } from '../api';
import { CLAIM_SETTLEMENT_DECISION, CLAIM_STATUS, PAYER_KIND, PREAUTH_RESPONSE_STATUS, PREAUTH_STATUS } from '../enums';
import { PRE_AUTH_SOURCE_TYPES, sourceKeySchema } from './nphies';
const attachmentNameSchema = z
    .string()
    .trim()
    .max(300)
    .refine((value) => !/[\u0000-\u001f\u007f\ud800-\udfff]/.test(value), 'Remove the control or unreadable characters');
const listCursorSchema = uuidSchema.optional();
export const payerListQuerySchema = z.object({
    q: z.string().trim().max(60).optional(),
    kind: z.enum(PAYER_KIND).optional(),
    includeInactive: z.enum(['true', 'false']).default('false'),
    availableInBranch: z.enum(['true', 'false']).default('false'),
    limit: z.coerce.number().int().min(1).max(500).default(200),
    cursor: listCursorSchema,
});
export const preAuthListAllQuerySchema = z.object({
    status: z.enum(PREAUTH_STATUS).optional(),
    patientId: uuidSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
    cursor: listCursorSchema,
});
export const claimListQuerySchema = z.object({
    status: z.enum(CLAIM_STATUS).optional(),
    payerId: uuidSchema.optional(),
    patientId: uuidSchema.optional(),
    branchId: uuidSchema.optional(),
    q: z.string().trim().max(60).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
    cursor: listCursorSchema,
});
export const preAuthDraftEditSchema = z
    .object({
    diagnosis: z.string().trim().min(3).max(600).optional(),
    procedure: z.string().trim().min(3).max(600).optional(),
    notes: z.string().trim().max(2000).optional(),
})
    .refine((value) => value.diagnosis !== undefined || value.procedure !== undefined || value.notes !== undefined, { message: 'Change at least one field' });
export const preAuthDiscardSchema = z.object({
    ids: z.array(uuidSchema).min(1).max(100),
});
export const upsertPayerSchema = z.object({
    code: z.string().trim().toUpperCase().min(2).max(24),
    name: z.string().trim().min(2).max(200),
    kind: z.enum(PAYER_KIND).default('insurance'),
    contactPerson: z.string().trim().max(160).optional(),
    phone: z.string().trim().max(20).optional(),
    email: z.string().trim().email().max(160).optional(),
    address: z.string().trim().max(400).optional(),
    /** Share the patient pays at the desk; the remainder goes on the claim. */
    coPayPct: z.number().min(0).max(100).default(0),
    creditLimitMinor: moneyMinorSchema.min(0).default(0),
    roomClassEntitlement: z.string().trim().max(40).nullable().optional(),
    exclusionsNote: z.string().max(4000).optional(),
    requiresPreAuth: z.boolean().default(false),
    claimTatDays: z.number().int().min(1).max(365).default(30),
    tpaPayerId: z.string().uuid().nullable().optional(),
    nphiesLicense: z.string().trim().max(40).nullable().optional(),
    nphiesOrgType: z.enum(['ins', 'tpa']).nullable().optional(),
    vatNumber: z.string().trim().max(40).nullable().optional(),
    legalName: z.string().trim().max(200).nullable().optional(),
    legalNameLocal: z.string().trim().max(200).nullable().optional(),
    billingAddress: z.string().trim().max(400).nullable().optional(),
    submissionWindowDays: z.number().int().min(1).max(3650).nullable().optional(),
    resubmissionWindowDays: z.number().int().min(1).max(3650).nullable().optional(),
    claimGrain: z.enum(['per_patient', 'per_payer_period']).nullable().optional(),
    isActive: z.boolean().default(true),
});
export const upsertCoverageSchema = z.object({
    patientId: z.string().uuid(),
    payerId: z.string().uuid(),
    policyNo: z.string().trim().min(1).max(80),
    employeeId: z.string().trim().max(80).optional(),
    relation: z.enum(['self', 'spouse', 'child', 'parent', 'other']).default('self'),
    planName: z.string().trim().max(160).optional(),
    validFrom: z.string().datetime().nullable().optional(),
    validTo: z.string().datetime().nullable().optional(),
    /** Overrides the payer default when the individual policy differs. */
    coPayPct: z.number().min(0).max(100).nullable().optional(),
    annualLimitMinor: moneyMinorSchema.min(0).nullable().optional(),
    planId: z.string().uuid().nullable().optional(),
    annualDeductibleMinor: moneyMinorSchema.min(0).nullable().optional(),
    memberCategory: z.string().trim().max(80).nullable().optional(),
    subscriberPatientId: z.string().uuid().nullable().optional(),
    policyHolderName: z.string().trim().max(160).nullable().optional(),
    isActive: z.boolean().default(true),
});
export const updateCoverageSchema = upsertCoverageSchema.omit({ patientId: true, payerId: true }).partial();
export const externalEligibilitySchema = z.object({
    reference: z.string().trim().min(2).max(80),
    date: z.string().date(),
});
export const BENEFIT_CATEGORY_PLAIN = ['consultation', 'diagnostics', 'medicines', 'inpatient', 'surgery', 'dental', 'optical', 'maternity'];
export const upsertPayerPlanSchema = z
    .object({
    code: z.string().trim().toUpperCase().min(1).max(24),
    name: z.string().trim().min(2).max(160),
    coverageTypeCode: z.string().trim().max(40).nullable().optional(),
    classValue: z.string().trim().max(80).nullable().optional(),
    network: z.string().trim().max(80).nullable().optional(),
    acceptedNetworks: z.array(z.string().trim().min(1).max(80)).max(20).default([]),
    patientSharePct: z.number().min(0).max(100).nullable().optional(),
    fixedPerVisitMinor: moneyMinorSchema.min(0).nullable().optional(),
    maxPerVisitMinor: moneyMinorSchema.min(0).nullable().optional(),
    maxPerDayMinor: moneyMinorSchema.min(0).nullable().optional(),
    maxPerYearMinor: moneyMinorSchema.min(0).nullable().optional(),
    annualDeductibleMinor: moneyMinorSchema.min(0).nullable().optional(),
    validFrom: z.string().date().nullable().optional(),
    validTo: z.string().date().nullable().optional(),
    isActive: z.boolean().default(true),
})
    .refine((value) => !value.validFrom || !value.validTo || value.validFrom <= value.validTo, { path: ['validTo'], message: 'The plan ends before it starts' });
export const preAuthSchema = z.object({
    patientId: z.string().uuid(),
    payerId: z.string().uuid(),
    admissionId: z.string().uuid().nullable().optional(),
    encounterId: z.string().uuid().nullable().optional(),
    requestedAmountMinor: moneyMinorSchema.min(0).default(0),
    diagnosis: z.string().trim().max(600).optional(),
    procedure: z.string().trim().max(600).optional(),
    notes: z.string().max(2000).optional(),
    authFrom: z.string().date().optional(),
    authTo: z.string().date().optional(),
    lines: z
        .array(z.object({
        chargeItemId: z.string().uuid(),
        quantity: z.number().positive().max(10_000).default(1),
        approvalNumber: z.string().trim().max(80).optional(),
        sourceType: z.enum(PRE_AUTH_SOURCE_TYPES).optional(),
        sourceId: uuidSchema.optional(),
    }))
        .max(100)
        .default([]),
});
export const INSURANCE_MODES = ['automatic', 'manual'];
export const AUTH_CHANNELS = ['nphies', 'manual'];
export const AUTH_HOLD_STATES = ['none', 'needs_send', 'waiting', 'answered'];
export const departmentInsuranceModeSchema = z.object({
    mode: z.enum(INSURANCE_MODES).nullable(),
});
export const facilityInsuranceModeSchema = departmentInsuranceModeSchema;
export const authorisationQuerySchema = z
    .object({
    encounterId: uuidSchema.optional(),
    admissionId: uuidSchema.optional(),
    invoiceId: uuidSchema.optional(),
})
    .refine((value) => [value.encounterId, value.admissionId, value.invoiceId].filter(Boolean).length === 1, { message: 'Give exactly one of encounterId, admissionId or invoiceId' });
export const authorisationSendSchema = z.object({
    payerId: uuidSchema.optional(),
    diagnosis: z.string().trim().max(600).optional(),
    resubmit: z.array(sourceKeySchema).max(200).default([]),
});
export const preAuthResponseSchema = z.object({
    status: z.enum(PREAUTH_RESPONSE_STATUS),
    preauthNo: z.string().trim().max(80).optional(),
    approvedAmountMinor: moneyMinorSchema.min(0).default(0),
    notes: z.string().max(2000).optional(),
});
export const createClaimSchema = z.object({
    payerId: z.string().uuid(),
    patientId: z.string().uuid(),
    admissionId: z.string().uuid().nullable().optional(),
    branchId: z.string().uuid().optional(),
    /** Invoices to bundle. Omit to pull every unclaimed panel invoice. */
    invoiceIds: z.array(z.string().uuid()).max(200).default([]),
    preauthNo: z.string().trim().max(80).optional(),
    attachments: z.array(attachmentNameSchema).max(50).default([]),
});
export const claimTransitionSchema = z.object({
    status: z.enum(CLAIM_STATUS),
    /** Attached when submitting, since the number often arrives after the claim. */
    preauthNo: z.string().trim().max(80).optional(),
    approvedAmountMinor: moneyMinorSchema.min(0).optional(),
    paidAmountMinor: moneyMinorSchema.min(0).optional(),
    rejectionReason: z.string().trim().max(600).optional(),
    queryNote: z.string().trim().max(1000).optional(),
    attachments: z.array(attachmentNameSchema).max(50).optional(),
});
export const claimManualAnswerSchema = z.object({
    lines: z
        .array(z.object({
        invoiceItemId: uuidSchema,
        approvedMinor: moneyMinorSchema.min(0),
        reasonCode: z.string().trim().max(40).optional(),
        reasonText: z.string().trim().max(400).optional(),
    }))
        .min(1)
        .max(500),
});
export const settleClaimLinesSchema = z.object({
    lines: z
        .array(z.object({
        claimLineId: uuidSchema,
        decision: z.enum(CLAIM_SETTLEMENT_DECISION),
        amountMinor: moneyMinorSchema.min(1),
        reason: z.string().trim().min(5).max(400),
        consentRef: z.string().trim().max(80).optional(),
        followUpOn: z.string().date().optional(),
    }))
        .min(1)
        .max(200),
});
export const reverseSettlementSchema = z.object({
    reason: z.string().trim().min(5).max(400),
});
export const closeClaimSchema = z.object({
    reason: z.string().trim().min(5).max(400),
});
export const REMITTANCE_METHODS = ['bank_transfer', 'cheque', 'cash', 'card', 'other'];
export const createRemittanceSchema = z.object({
    payerId: uuidSchema,
    reference: z.string().trim().min(1).max(80),
    paymentDate: z.string().date(),
    method: z.enum(REMITTANCE_METHODS).default('bank_transfer'),
    bankRef: z.string().trim().max(80).optional(),
    depositAccountCode: z.string().trim().max(20).optional(),
    totalMinor: moneyMinorSchema.min(1),
    feeMinor: moneyMinorSchema.min(0).default(0),
    withholdingTaxMinor: moneyMinorSchema.min(0).default(0),
    periodFrom: z.string().date().optional(),
    periodTo: z.string().date().optional(),
    note: z.string().trim().max(400).optional(),
});
export const remittanceListQuerySchema = z.object({
    source: z.enum(['nphies', 'manual']).optional(),
    status: z.enum(['received', 'allocated', 'partially_allocated', 'unmatched', 'needs_review']).optional(),
    payerId: uuidSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.string().uuid().optional(),
});
export const allocateRemittanceSchema = z.object({
    lines: z
        .array(z.object({
        claimId: uuidSchema,
        paidMinor: moneyMinorSchema.min(0),
        deductedMinor: moneyMinorSchema.min(0).default(0),
        reason: z.string().trim().max(400).optional(),
    }))
        .min(1)
        .max(200),
});
export const bulkClaimSchema = z.object({
    payerId: uuidSchema,
    from: z.string().date(),
    to: z.string().date(),
    branchId: uuidSchema.optional(),
});
export const unclaimedQuerySchema = z.object({
    payerId: uuidSchema.optional(),
    branchId: uuidSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().uuid().optional(),
});
export const claimExportQuerySchema = z.object({
    from: z.string().date(),
    to: z.string().date(),
    payerId: uuidSchema.optional(),
    branchId: uuidSchema.optional(),
});
export const revenueCycleQuerySchema = claimExportQuerySchema;
export const payerStatementQuerySchema = z.object({
    from: z.string().date(),
    to: z.string().date(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
    cursor: z.string().uuid().optional(),
});
const monthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Give the month as YYYY-MM');
export const payerInvoiceDueQuerySchema = z.object({ month: monthSchema });
export const issuePayerInvoiceSchema = z.object({ payerId: uuidSchema, month: monthSchema });
export const payerInvoiceListQuerySchema = z.object({
    kind: z.enum(['invoice', 'credit_note']).optional(),
    payerId: uuidSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: uuidSchema.optional(),
});
export const panelBillQuerySchema = z.object({
    from: z.string().date(),
    to: z.string().date(),
    branchId: uuidSchema.optional(),
});
export const upsertPayerServiceCodeSchema = z
    .object({
    chargeItemId: uuidSchema.nullable().optional(),
    productId: uuidSchema.nullable().optional(),
    code: z.string().trim().min(1).max(80),
    system: z.string().trim().max(200).nullable().optional(),
    display: z.string().trim().max(200).nullable().optional(),
    isNonStandard: z.boolean().default(false),
    notes: z.string().trim().max(400).nullable().optional(),
    isActive: z.boolean().default(true),
})
    .refine((value) => Boolean(value.chargeItemId) !== Boolean(value.productId), { path: ['chargeItemId'], message: 'Choose one service or one product' });
//# sourceMappingURL=insurance.js.map